import { createSlice } from "@reduxjs/toolkit";
import {
    getAllTickets,
    getTicketByID,
    getTicketsByEmployeeID,
    getMyCreatedTickets,
    getAssignedTickets,
    createTicket,
    updateTicket,
    deleteTicket,
    addTicketComment,
    deleteTicketComment,
    getTicketStats,
    escalateTicket,
    uploadTicketAttachment,
    deleteTicketAttachment
} from "./thunk";

interface TicketState {
    ticketsList: any[];
    ticketDetails: any;
    ticketStats: any;
    loading: boolean;
    error: string | null;
}

const initialState: TicketState = {
    ticketsList: [],
    ticketDetails: null,
    ticketStats: null,
    loading: false,
    error: null,
};

const ticketSlice = createSlice({
    name: "tickets",
    initialState,
    reducers: {
        clearTicketDetails: (state) => {
            state.ticketDetails = null;
        },
        clearTicketError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Get All Tickets
        builder.addCase(getAllTickets.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getAllTickets.fulfilled, (state, action) => {
            state.loading = false;
            state.ticketsList = action.payload || [];
        });
        builder.addCase(getAllTickets.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to fetch tickets";
        });

        // Get Ticket By ID
        builder.addCase(getTicketByID.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getTicketByID.fulfilled, (state, action) => {
            state.loading = false;
            state.ticketDetails = action.payload;
        });
        builder.addCase(getTicketByID.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to fetch ticket details";
        });

        // Get Tickets By Employee ID
        builder.addCase(getTicketsByEmployeeID.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getTicketsByEmployeeID.fulfilled, (state, action) => {
            state.loading = false;
            state.ticketsList = action.payload || [];
        });
        builder.addCase(getTicketsByEmployeeID.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to fetch employee tickets";
        });

        // Get My Created Tickets
        builder.addCase(getMyCreatedTickets.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getMyCreatedTickets.fulfilled, (state, action) => {
            state.loading = false;
            state.ticketsList = action.payload || [];
        });
        builder.addCase(getMyCreatedTickets.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to fetch your created tickets";
        });

        // Get Assigned Tickets
        builder.addCase(getAssignedTickets.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getAssignedTickets.fulfilled, (state, action) => {
            state.loading = false;
            state.ticketsList = action.payload || [];
        });
        builder.addCase(getAssignedTickets.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to fetch assigned tickets";
        });

        // Create Ticket
        builder.addCase(createTicket.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(createTicket.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload) {
                state.ticketsList.unshift(action.payload);
            }
        });
        builder.addCase(createTicket.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to create ticket";
        });

        // Update Ticket
        builder.addCase(updateTicket.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateTicket.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload) {
                const index = state.ticketsList.findIndex(ticket => ticket.id === action.payload.id);
                if (index !== -1) {
                    state.ticketsList[index] = action.payload;
                }
                if (state.ticketDetails && state.ticketDetails.id === action.payload.id) {
                    state.ticketDetails = action.payload;
                }
            }
        });
        builder.addCase(updateTicket.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to update ticket";
        });

        // Delete Ticket
        builder.addCase(deleteTicket.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteTicket.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload) {
                state.ticketsList = state.ticketsList.filter(ticket => ticket.id !== action.payload.id);
                if (state.ticketDetails && state.ticketDetails.id === action.payload.id) {
                    state.ticketDetails = null;
                }
            }
        });
        builder.addCase(deleteTicket.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to delete ticket";
        });

        // Add Ticket Comment
        builder.addCase(addTicketComment.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(addTicketComment.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload && state.ticketDetails) {
                if (!state.ticketDetails.comments) {
                    state.ticketDetails.comments = [];
                }
                state.ticketDetails.comments.push(action.payload);
            }
        });
        builder.addCase(addTicketComment.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to add comment";
        });

        // Delete Ticket Comment
        builder.addCase(deleteTicketComment.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteTicketComment.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload && state.ticketDetails && state.ticketDetails.comments) {
                state.ticketDetails.comments = state.ticketDetails.comments.filter(
                    (comment: any) => comment.id !== action.payload.id
                );
            }
        });
        builder.addCase(deleteTicketComment.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to delete comment";
        });

        // Get Ticket Stats
        builder.addCase(getTicketStats.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getTicketStats.fulfilled, (state, action) => {
            state.loading = false;
            state.ticketStats = action.payload;
        });
        builder.addCase(getTicketStats.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to fetch ticket stats";
        });

        // Escalate Ticket
        builder.addCase(escalateTicket.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(escalateTicket.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload) {
                const index = state.ticketsList.findIndex(ticket => ticket.id === action.payload.id);
                if (index !== -1) {
                    state.ticketsList[index] = action.payload;
                }
                if (state.ticketDetails && state.ticketDetails.id === action.payload.id) {
                    state.ticketDetails = action.payload;
                }
            }
        });
        builder.addCase(escalateTicket.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to escalate ticket";
        });

        // Upload Ticket Attachment
        builder.addCase(uploadTicketAttachment.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(uploadTicketAttachment.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload && state.ticketDetails) {
                if (!state.ticketDetails.attachments) {
                    state.ticketDetails.attachments = [];
                }
                state.ticketDetails.attachments.push(action.payload);
            }
        });
        builder.addCase(uploadTicketAttachment.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to upload attachment";
        });

        // Delete Ticket Attachment
        builder.addCase(deleteTicketAttachment.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteTicketAttachment.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload && state.ticketDetails && state.ticketDetails.attachments) {
                state.ticketDetails.attachments = state.ticketDetails.attachments.filter(
                    (attachment: any) => attachment.id !== action.payload.id
                );
            }
        });
        builder.addCase(deleteTicketAttachment.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to delete attachment";
        });
    },
});

export const { clearTicketDetails, clearTicketError } = ticketSlice.actions;
export default ticketSlice.reducer;
